import axios, {
  type AxiosError,
  type AxiosInstance,
  type InternalAxiosRequestConfig,
} from 'axios';
import { getStoredToken } from '../auth/tokenStorage';
import { getStoredRefreshToken } from '../auth/refreshTokenStorage';
import { readAccessTokenExpiry } from '../auth/parseAccessToken';
import { parseAccessTokenPayload } from '../auth/parseAccessToken';
import { useLocationStore } from '../location/locationStore';
import { ascendIdForLocationUuid } from '../location/resolveLocationHeaderValue';
import { isExpiredTokenError, readApiError, requiresFreshSignIn } from './errors';

let accountApi: AxiosInstance | null = null;
let mainApi: AxiosInstance | null = null;
/** Cached practice-management location key (`ascend_id`) for core-api-az's `X-Location-Id`. */
let activeLocationHeaderValue: string | null = null;

/** Refresh this long before `exp` so an in-flight request never lands on a dead token. */
const PROACTIVE_REFRESH_WINDOW_MS = 120_000;

/**
 * Public endpoints: the caller has no usable token, which is the point. Sending a
 * stale Bearer here makes DRF reject with `authentication_failed` before the view runs.
 */
const PUBLIC_AUTH_PATHS = ['/auth/handoff/redeem', '/auth/refresh'];

export function getAccountApi(): AxiosInstance {
  if (!accountApi) {
    throw new Error('@elva/platform: API not initialized. Call initPlatformApi first.');
  }
  return accountApi;
}

export function getMainApi(): AxiosInstance {
  if (!mainApi) {
    throw new Error('@elva/platform: API not initialized. Call initPlatformApi first.');
  }
  return mainApi;
}

/** Set core-api-az's scoped location header value — the location's `ascend_id`, not its UUID. */
export function setLocationHeader(ascendId: string | null): void {
  activeLocationHeaderValue = ascendId;
}

export function removeLocationHeader(): void {
  activeLocationHeaderValue = null;
}

export function setAuthToken(token: string): void {
  const bearer = `Bearer ${token}`;
  getMainApi().defaults.headers.common.Authorization = bearer;
  getAccountApi().defaults.headers.common.Authorization = bearer;
}

export function removeAuthToken(): void {
  delete getMainApi().defaults.headers.common.Authorization;
  delete getAccountApi().defaults.headers.common.Authorization;
}

type RetriableRequestConfig = InternalAxiosRequestConfig & { _retry?: boolean };

let isRedirectingToLogin = false;

function isPublicAuthPath(config: InternalAxiosRequestConfig | undefined): boolean {
  const url = config?.url ?? '';
  return PUBLIC_AUTH_PATHS.some((path) => url.includes(path));
}

/** Practice-management key (`ascend_id`) for core-api-az, which keys locations by `external_id`. */
async function resolveLocationHeaderValue(): Promise<string | null> {
  if (activeLocationHeaderValue) {
    return activeLocationHeaderValue;
  }

  const uuid = useLocationStore.getState().getSelectedLocationUuid();
  if (!uuid) {
    return null;
  }

  const token = getStoredToken();
  if (token) {
    const payload = parseAccessTokenPayload(token);
    if (payload) {
      return ascendIdForLocationUuid(uuid, payload.locations, payload.access);
    }
  }

  const { useAuthStore } = await import('../auth/authStore');
  const { locations, access } = useAuthStore.getState();
  return ascendIdForLocationUuid(uuid, locations, access);
}

/**
 * Account-service location key — the account API addresses locations by their own UUID
 * (`access.location` / `locations[].id`), never by the practice-management `ascend_id`.
 * Sending `ascend_id` here leaves `access.location` on whatever UUID the token was last
 * signed with, since the account API can't resolve an `ascend_id` back to that UUID.
 */
function resolveLocationUuidHeaderValue(): string | null {
  return useLocationStore.getState().getSelectedLocationUuid();
}

async function refreshViaStore(): Promise<string | null> {
  const { useAuthStore } = await import('../auth/authStore');
  return useAuthStore.getState().refreshSession();
}

async function signInAgain(): Promise<void> {
  if (isRedirectingToLogin) return;
  isRedirectingToLogin = true;
  const { useAuthStore } = await import('../auth/authStore');
  await useAuthStore.getState().logout();
}

async function attachRequestHeaders(
  api: AxiosInstance,
  config: InternalAxiosRequestConfig,
  isAccountApi: boolean,
): Promise<InternalAxiosRequestConfig> {
  const locationHeaderValue = isAccountApi
    ? resolveLocationUuidHeaderValue()
    : await resolveLocationHeaderValue();

  // Required on /auth/refresh too, or the new token comes back unscoped.
  if (locationHeaderValue) {
    config.headers['X-Location-Id'] = locationHeaderValue;
  }

  if (isPublicAuthPath(config)) {
    delete config.headers.Authorization;
    return config;
  }

  let token = getStoredToken();

  if (token && getStoredRefreshToken()) {
    const expiresAt = readAccessTokenExpiry(token);
    if (expiresAt !== null && expiresAt - Date.now() < PROACTIVE_REFRESH_WINDOW_MS) {
      try {
        token = (await refreshViaStore()) ?? token;
      } catch {
        // Let the request go out and fail; the response interceptor decides what to do.
      }
    }
  }

  if (token) {
    const bearer = `Bearer ${token}`;
    api.defaults.headers.common.Authorization = bearer;
    config.headers.Authorization = bearer;
  }

  return config;
}

function attachAuthResponseInterceptor(
  api: AxiosInstance,
  options: { ownsSession: boolean },
): void {
  api.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      const originalRequest = error.config as RetriableRequestConfig | undefined;
      const apiError = readApiError(error);

      if (!originalRequest || isPublicAuthPath(originalRequest)) {
        return await Promise.reject(error);
      }

      const canRetry = !originalRequest._retry;
      const shouldRefresh =
        isExpiredTokenError(apiError) ||
        (apiError?.code === 'not_authenticated' && getStoredRefreshToken() !== null);

      if (shouldRefresh && canRetry) {
        originalRequest._retry = true;
        try {
          const token = await refreshViaStore();
          if (token) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return await api.request(originalRequest);
          }
        } catch {
          if (options.ownsSession) {
            await signInAgain();
          }
          return await Promise.reject(error);
        }
      }

      // Only the account API owns the OAuth session — a main-api 401 (e.g. env mismatch)
      // must not wipe a valid account login.
      if (requiresFreshSignIn(apiError) && options.ownsSession) {
        await signInAgain();
      }

      return await Promise.reject(error);
    },
  );
}

export function createApiInstances(
  accountApiUrl: string,
  mainApiUrl: string,
  timeout: number,
): { account: AxiosInstance; main: AxiosInstance } {
  isRedirectingToLogin = false;

  // v2 is Bearer-only — never send account-app session cookies on cross-origin calls.
  const account = axios.create({
    baseURL: accountApiUrl,
    timeout,
    withCredentials: false,
    headers: { 'Content-Type': 'application/json' },
  });

  const main = axios.create({
    baseURL: mainApiUrl,
    timeout,
    headers: { 'Content-Type': 'application/json' },
  });

  account.interceptors.request.use((config) => attachRequestHeaders(account, config, true));
  main.interceptors.request.use((config) => attachRequestHeaders(main, config, false));

  attachAuthResponseInterceptor(account, { ownsSession: true });
  attachAuthResponseInterceptor(main, { ownsSession: false });

  accountApi = account;
  mainApi = main;

  return { account, main };
}
